import { FC, useMemo } from "react";

import { Box, Card, HStack, Pressable, Text, VStack } from "@/components/ui";

import {
  fillFor,
  formatMargin,
  formatPercent,
  RAMPS,
  STRIPE,
  TOSSUP,
} from "./colors";
import InfoTip from "./InfoTip";
import { shortName } from "./survey";
import type { Call, Candidate, Office } from "./types";
import { OFFICE_LABEL, RaceRow, raceVerdict, type Verdict } from "./view";

interface RaceListProps {
  office: Office;
  onSelect: (state: string) => void;
  /** The compared source is the count. */
  results: boolean;
  rows: RaceRow[];
  sourceLabel: string | null;
}

const HINT =
  "Only seats on the 2026 ballot are listed. A striped swatch and the FLIP tag mark races where the projected leader differs from the party holding the seat. Click a row to open the state and its county map.";

const RESULTS_HINT =
  "Foresight's margin and call next to the count's: misses first, then the largest gaps. Gap: Foresight's margin minus the count's, in points of the two-party vote (R+3.0: Foresight was 3 points more Republican). Click a row to open the state.";

/** Closest races first; races with no reading at the bottom. */
const sortRows = (rows: RaceRow[]) =>
  [...rows].sort((a, b) => {
    const pa = a.call.leftProbability;
    const pb = b.call.leftProbability;
    if (pa === null && pb === null)
      return a.state.name.localeCompare(b.state.name);
    if (pa === null) return 1;
    if (pb === null) return -1;
    return Math.abs(pa - 0.5) - Math.abs(pb - 0.5);
  });

/** Misses first, then the largest gaps; races without a gap at the bottom. */
export const byMisses = <T,>(items: T[], verdictOf: (item: T) => Verdict) =>
  items
    .map((item) => ({ item, verdict: verdictOf(item) }))
    .sort((a, b) => {
      if (a.verdict.miss !== b.verdict.miss) return a.verdict.miss ? -1 : 1;
      const ga = a.verdict.gap;
      const gb = b.verdict.gap;
      if (ga === null || gb === null) return ga === null ? 1 : -1;
      return Math.abs(gb) - Math.abs(ga);
    });

const nameOf = (candidate: Candidate | null) =>
  candidate ? shortName(candidate) : "TBD";

/** A map color, with the 45° stripes of a flip or a missed call. */
export const FillSwatch: FC<{ fill: string; striped: boolean }> = ({
  fill,
  striped,
}) => (
  <Box
    className="w-3.5 h-3.5 rounded-sm overflow-hidden"
    style={{ backgroundColor: fill }}
  >
    {striped &&
      [-3, 3].map((offset) => (
        <Box
          className="absolute"
          key={offset}
          style={{
            backgroundColor: STRIPE,
            height: 2,
            left: -3,
            top: 6 + offset,
            transform: [{ rotate: "-45deg" }],
            width: 20,
          }}
        />
      ))}
  </Box>
);

/** Map color of a race; projected flips get the same 45° stripes. */
export const Swatch: FC<{ call: Call; flip: boolean }> = ({ call, flip }) => (
  <FillSwatch fill={fillFor(call)} striped={flip} />
);

/** The race's color: its winner, grey until called. */
export const CountSwatch: FC<{ verdict: Verdict }> = ({ verdict }) => (
  <FillSwatch
    fill={verdict.actual ? RAMPS[verdict.actual].likely : TOSSUP}
    striped={verdict.miss}
  />
);

export const Tag: FC<{ label: string }> = ({ label }) => (
  <Text className="text-2xs font-bold tracking-wide text-typography-700 border border-outline-300 rounded px-1">
    {label}
  </Text>
);

const RaceList: FC<RaceListProps> = ({
  office,
  onSelect,
  results,
  rows,
  sourceLabel,
}) => {
  const sorted = useMemo<{ item: RaceRow; verdict: Verdict | null }[]>(
    () =>
      results
        ? byMisses(rows, raceVerdict)
        : sortRows(rows).map((item) => ({ item, verdict: null })),
    [results, rows]
  );

  return (
    <Card className="p-5 rounded-2xl border border-outline-200">
      <VStack className="gap-3">
        <HStack className="items-center gap-1">
          <Text className="text-sm font-semibold text-typography-900">
            {office === "house"
              ? "Generic ballot by state"
              : `${OFFICE_LABEL[office]} races, ${results ? "misses" : "closest"} first`}
          </Text>
          <InfoTip text={results ? RESULTS_HINT : HINT} />
        </HStack>

        <HStack className="px-1 gap-2">
          <Text className="flex-1 text-xs text-typography-400">Race</Text>
          <Text className="w-20 text-right text-xs text-typography-400">
            Foresight
          </Text>
          {sourceLabel && (
            <Text
              className="w-20 text-right text-xs text-typography-400"
              numberOfLines={1}
            >
              {results ? "Count" : sourceLabel}
            </Text>
          )}
          {results && (
            <Text className="w-12 text-right text-xs text-typography-400">
              Gap
            </Text>
          )}
        </HStack>

        <VStack className="gap-0.5">
          {sorted.map(({ item: row, verdict }) => (
            <Pressable
              className="flex-row items-center gap-2 px-1 py-1.5 rounded-md hover:bg-background-50"
              key={`${row.state.abbr}-${row.race?.special ? "special" : "regular"}`}
              onPress={() => onSelect(row.state.abbr)}
            >
              {verdict ? (
                <CountSwatch verdict={verdict} />
              ) : (
                <Swatch call={row.call} flip={row.flip} />
              )}
              <VStack className="flex-1 min-w-0">
                <HStack className="items-center gap-1.5">
                  <Text
                    className="text-sm font-medium text-typography-900"
                    numberOfLines={1}
                  >
                    {row.state.name}
                  </Text>
                  {verdict
                    ? verdict.miss && <Tag label="MISS" />
                    : row.flip && <Tag label="FLIP" />}
                </HStack>
                {row.race && (
                  <Text
                    className="text-xs text-typography-500"
                    numberOfLines={1}
                  >
                    {nameOf(row.race.left)} ({row.leftParty}) vs{" "}
                    {nameOf(row.race.right)} (R) · held by{" "}
                    {row.race.incumbentParty}
                  </Text>
                )}
              </VStack>
              <VStack className="w-20 items-end">
                <Text className="text-sm font-semibold text-typography-900">
                  {row.estimate
                    ? formatMargin(row.estimate.margin, row.leftParty)
                    : "—"}
                </Text>
                {row.estimate && (
                  <Text className="text-xs text-typography-500">
                    {verdict
                      ? `call ${verdict.ours ?? "even"}`
                      : `${formatPercent(row.estimate.leftProbability)} ${row.leftParty}`}
                  </Text>
                )}
              </VStack>
              {verdict ? (
                <>
                  <VStack className="w-20 items-end">
                    <Text className="text-sm text-typography-700">
                      {formatMargin(
                        row.external?.margin ?? null,
                        row.leftParty
                      )}
                    </Text>
                    {row.external?.result && (
                      <Text className="text-xs text-typography-500">
                        {verdict.actual
                          ? `called ${verdict.actual}`
                          : `${row.external.result.reporting}% in`}
                      </Text>
                    )}
                  </VStack>
                  <Text className="w-12 text-right text-xs text-typography-500">
                    {verdict.gap === null
                      ? "—"
                      : formatMargin(verdict.gap, row.leftParty)}
                  </Text>
                </>
              ) : (
                sourceLabel && (
                  <Text className="w-20 text-right text-sm text-typography-700">
                    {row.externalProbability === null
                      ? "—"
                      : `${formatPercent(row.externalProbability)} ${row.leftParty}`}
                  </Text>
                )
              )}
            </Pressable>
          ))}
        </VStack>
      </VStack>
    </Card>
  );
};

export default RaceList;
